import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Sparkles, Award, BookOpen, ChevronDown, ChevronUp } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function md(text) {
  if (!text) return null;
  return text.split("\n").map((l, i) => {
    const k = `L${i}-${l.slice(0, 24)}`;
    if (l.startsWith("## ")) return <h4 key={k} className="font-heading text-[15px] text-[#8B4513] mt-3 mb-1">{l.slice(3)}</h4>;
    if (l.startsWith("# ")) return <h3 key={k} className="font-heading text-lg text-[#5B3A0F] mt-3 mb-2">{l.slice(2)}</h3>;
    if (l.startsWith("- ") || l.startsWith("* ")) return <li key={k} className="text-sm text-[#3A2A0F] ml-6 list-disc">{l.slice(2)}</li>;
    if (!l.trim()) return <div key={k} className="h-2" />;
    return <p key={k} className="text-sm text-[#3A2A0F] leading-relaxed">{l}</p>;
  });
}

function fruitionColor(pct) {
  if (pct >= 80) return { bg: "#5C8A3F", text: "#FFF" };
  if (pct >= 60) return { bg: "#B08A3E", text: "#3A2A0F" };
  if (pct >= 40) return { bg: "#C15C3D", text: "#FFF" };
  return { bg: "#8A857B", text: "#FFF" };
}

export default function YogaAtlasPanel({ profileId }) {
  const [atlas, setAtlas] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedName, setExpandedName] = useState(null);
  const [synth, setSynth] = useState({});          // { yoga_name: {loading, data} }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/profiles/${profileId}/yogas/atlas`);
      setAtlas(r.data);
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Failed to load Yoga Atlas");
    } finally { setLoading(false); }
  }, [profileId]);
  useEffect(() => { load(); }, [load]);

  const toggle = (name) => {
    setExpandedName((prev) => prev === name ? null : name);
  };

  const unlockMaster = async (name) => {
    if (synth[name]?.data) return;   // already synthesised — free
    setSynth((s) => ({ ...s, [name]: { loading: true } }));
    try {
      const r = await axios.post(`${API}/profiles/${profileId}/yogas/atlas/synthesise`,
        { yoga_name: name });
      setSynth((s) => ({ ...s, [name]: { loading: false, data: r.data } }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Synthesis failed");
      setSynth((s) => ({ ...s, [name]: { loading: false } }));
    }
  };

  if (loading) return (
    <div className="text-[#8B4513] font-mono text-sm flex items-center gap-2 py-8"
         data-testid="yoga-atlas-loading">
      <Loader2 size={14} className="animate-spin" />
      Scanning 300 Raman yogas across D1 → D60 (~30-60 s first time)…
    </div>
  );
  if (!atlas) return null;

  return (
    <div className="space-y-5" data-testid="yoga-atlas-panel">
      {/* header */}
      <div className="border-2 border-[#B08A3E] rounded-md p-5 bg-gradient-to-br from-[#FBF3DC] to-[#F0E6C8] shadow-md">
        <div className="flex items-center gap-2 mb-1">
          <Award size={18} className="text-[#8B4513]" />
          <div className="text-[10px] uppercase tracking-[0.4em] font-mono text-[#8B4513]">
            B.V. Raman · 300 Yoga Master Atlas
          </div>
        </div>
        <div className="font-heading text-2xl text-[#3A2A0F] mb-2">
          {atlas.yogas_detected} yogas detected · scanned across {atlas.vargas_scanned?.length || 15} divisional charts
        </div>
        <div className="text-xs font-mono text-[#5B4A2A] leading-relaxed">
          Every canonical Raman yoga is tested against D1 → D60. Each row
          shows the classical definition, life-influence, and a
          fruition-percentage computed from planetary dignity and
          Shadbala. Click any yoga to unlock a detailed
          "Experienced Master" reading (career, health, wealth, timing) —
          on-demand and cached, so credits are only spent once per yoga.
        </div>
      </div>

      {/* atlas rows */}
      {atlas.yogas_detected === 0 && (
        <div className="border border-dashed border-[#B08A3E]/50 rounded-md p-6 text-center text-sm text-[#5B4A2A] italic">
          No canonical Raman yogas were detected in any of the 15 divisional charts for this native.
        </div>
      )}
      <div className="space-y-3">
        {(atlas.yogas || []).map((y) => {
          const open = expandedName === y.name;
          const col = fruitionColor(y.max_fruition_pct);
          const s = synth[y.name] || {};
          return (
            <div key={y.name}
                 data-testid={`yoga-atlas-row-${y.name.replace(/[^a-zA-Z0-9]/g,'_')}`}
                 className="border border-[#B08A3E]/60 rounded-md bg-white/70 shadow-sm overflow-hidden">
              <button onClick={() => toggle(y.name)}
                      className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-[#F0E6C8]/60 transition-colors">
                <div className="flex-shrink-0 flex flex-col items-center justify-center rounded-full h-14 w-14 shadow-sm"
                     style={{ background: col.bg, color: col.text }}
                     title={`Peak fruition ${y.max_fruition_pct}%`}>
                  <span className="font-heading text-lg leading-none">{y.max_fruition_pct}%</span>
                  <span className="text-[8px] uppercase tracking-widest mt-0.5">Fruition</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-heading text-lg text-[#3A2A0F]">{y.name}</span>
                    <span className="text-[9px] uppercase tracking-widest font-mono border border-[#B08A3E]/50 text-[#8B4513] px-1.5 py-0.5 rounded-sm">
                      {y.category}
                    </span>
                    <span className="text-[9px] uppercase tracking-widest font-mono text-[#5B4A2A]">
                      Detected in {y.occurrences?.length || 0} varga(s)
                    </span>
                  </div>
                  <div className="text-xs text-[#5B4A2A] mt-1 leading-snug line-clamp-1">
                    {y.definition}
                  </div>
                </div>
                {open ? <ChevronUp size={16} className="text-[#8B4513]" />
                       : <ChevronDown size={16} className="text-[#8B4513]" />}
              </button>

              {open && (
                <div className="border-t border-[#B08A3E]/40 bg-[#FDF7E4] p-4 space-y-3"
                     data-testid={`yoga-atlas-detail-${y.name.replace(/[^a-zA-Z0-9]/g,'_')}`}>
                  <div>
                    <div className="text-[9px] uppercase tracking-widest font-mono text-[#8B4513] mb-1">Definition</div>
                    <div className="text-sm text-[#3A2A0F]">{y.definition || "—"}</div>
                  </div>
                  <div>
                    <div className="text-[9px] uppercase tracking-widest font-mono text-[#8B4513] mb-1">B.V. Raman · Life Influence</div>
                    <div className="text-sm text-[#3A2A0F]">{y.influence || "—"}</div>
                  </div>
                  <div>
                    <div className="text-[9px] uppercase tracking-widest font-mono text-[#8B4513] mb-1">Varga occurrences</div>
                    <div className="flex flex-wrap gap-1.5">
                      {(y.occurrences || []).map((o, i) => (
                        <span key={`${y.name}-${o.varga}-${i}`}
                              title={o.involved?.join(", ") || ""}
                              className="text-[10px] font-mono px-2 py-0.5 rounded-sm border border-[#B08A3E]/50 bg-white/70 text-[#3A2A0F]">
                          {o.varga} · {o.fruition_pct}%
                          {o.involved?.length ? ` · ${o.involved.join("+")}` : ""}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Master synthesis */}
                  <div className="pt-2 border-t border-[#B08A3E]/30">
                    {!s.data && (
                      <button onClick={() => unlockMaster(y.name)} disabled={s.loading}
                              data-testid={`yoga-atlas-unlock-${y.name.replace(/[^a-zA-Z0-9]/g,'_')}`}
                              className="bg-gradient-to-r from-[#8B4513] to-[#B08A3E] text-white font-mono text-[10px] uppercase tracking-widest px-3 py-2 rounded-sm hover:brightness-110 disabled:opacity-50 flex items-center gap-2">
                        {s.loading
                          ? <><Loader2 size={12} className="animate-spin" /> Consulting the Master…</>
                          : <><Sparkles size={12} /> Unlock Experienced Master reading · ≈ ₹8</>}
                      </button>
                    )}
                    {s.data && (
                      <div className="bg-white/70 border border-[#B08A3E]/40 rounded-sm p-3"
                           data-testid={`yoga-atlas-synth-${y.name.replace(/[^a-zA-Z0-9]/g,'_')}`}>
                        <div className="flex items-center gap-2 mb-2">
                          <BookOpen size={12} className="text-[#8B4513]" />
                          <div className="text-[9px] uppercase tracking-widest font-mono text-[#8B4513]">
                            Experienced Master · {y.name}
                          </div>
                        </div>
                        {md(s.data.narrative)}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
